import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useTier } from '@/hooks/useTier';
import { useAccountantRole } from '@/hooks/useRole';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Logo } from './Logo';
import { LanguageSwitcher } from './LanguageSwitcher';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { LogOut, Settings, User, Crown, Zap, Sparkles, Sun, Moon } from 'lucide-react';
import { applyTheme } from '@/lib/utils/theme';
import { updateSettings } from '@/lib/api/settings';

export function Header() {
  const { t } = useTranslation('common');
  const { user, signOut } = useAuth();
  const { tier, isPro } = useTier();
  const { isAccountant } = useAccountantRole();
  const [isDark, setIsDark] = useState(() =>
    document.documentElement.classList.contains('dark')
  );

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains('dark'));
    });
    observer.observe(document.documentElement, { attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  function toggleTheme() {
    const next = isDark ? 'light' : 'dark';
    applyTheme(next);
    setIsDark(!isDark);
    if (user?.profile?.id) {
      updateSettings(user.profile.id, { theme_preference: next }).catch(console.error);
    }
  }

  const firstName = user?.profile?.first_name;
  const lastName = user?.profile?.last_name;
  const personalName = firstName
    ? `${firstName}${lastName ? ' ' + lastName : ''}`
    : user?.profile?.business_name || null;

  const userInitials = firstName
    ? `${firstName[0]}${lastName ? lastName[0] : ''}`.toUpperCase()
    : user?.email?.split('@')[0].slice(0, 2).toUpperCase() || 'U';

  return (
    <header className="border-b bg-background sticky top-0 z-50">
      <div className="flex h-16 items-center px-4 gap-2 md:gap-4">
        {/* Logo */}
        <Link to="/dashboard" className="flex items-center">
          <Logo size="sm" />
        </Link>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Language Switcher */}
        <LanguageSwitcher />

        {/* Tier Badge - hidden on small screens and for accountants */}
        {tier && !isAccountant && (
          <Link to="/plans" className="hidden sm:flex">
            <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs md:text-sm font-medium hover:bg-primary/20 transition-colors cursor-pointer">
              {isPro && <Crown className="h-3 w-3 md:h-3.5 md:w-3.5" />}
              {tier === 'lite' && <Zap className="h-3 w-3 md:h-3.5 md:w-3.5" />}
              {tier === 'free' && <Sparkles className="h-3 w-3 md:h-3.5 md:w-3.5" />}
              <span className="capitalize">{tier}</span>
            </div>
          </Link>
        )}

        {/* User Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="relative h-9 w-9 rounded-full">
              <Avatar className="h-9 w-9">
                <AvatarFallback className="bg-primary text-primary-foreground">
                  {userInitials}
                </AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium leading-none">
                  {personalName || 'Utente'}
                </p>
                <p className="text-xs leading-none text-muted-foreground">
                  {user?.email}
                </p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/profile" className="cursor-pointer">
                <User className="mr-2 h-4 w-4" />
                {t('nav.profile')}
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/settings" className="cursor-pointer">
                <Settings className="mr-2 h-4 w-4" />
                {t('nav.settings')}
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={toggleTheme} className="cursor-pointer">
              {isDark ? <Sun className="mr-2 h-4 w-4" /> : <Moon className="mr-2 h-4 w-4" />}
              {isDark ? t('theme.light') : t('theme.dark')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => signOut()}
              className="text-destructive focus:text-destructive cursor-pointer"
            >
              <LogOut className="mr-2 h-4 w-4" />
              {t('auth.logout')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
